// One place every error in the dashboard gets reported from.
//
// The old reporter only listened on window.onerror + unhandledrejection, i.e.
// errors NOBODY catches. But `bot/dashboard/src` has ~145 catch blocks that
// swallow silently and ~114 paths that show the user an error and report it
// nowhere. So the errors a user actually SEES were the ones we could never
// see — which is exactly how "Unexpected token '<', "<!DOCTYPE "..." (caught,
// rendered into the compose banner) left no trace anywhere and had to be
// reconstructed from Apache's logs days later.
//
// Call this from any catch that a human would want to know about. It never
// throws and never returns anything — a failure to report must not become the
// thing that breaks the feature.

const REPORT_URL = '/api/app-errors';

// De-dupe identical errors client-side. A fetch inside a broken useEffect can
// fire hundreds of times a second; without this the server rate-limiter would
// shed them (fine) but the browser would still burn a request each time.
const DEDUPE_WINDOW_MS = 60_000;
const recent = new Map();   // key -> last reported at

const DEDUPE_MAX = 200;

function shouldReport(key) {
  const now = Date.now();
  const last = recent.get(key) || 0;
  if (now - last < DEDUPE_WINDOW_MS) return false;
  // Bound the map. A burst of DISTINCT errors (an id baked into each message)
  // never repeats a key and never ages out within the window, so the age-based
  // sweep alone could grow unbounded while running an O(n) pass per insert.
  // Map preserves insertion order, so evicting the oldest entry is O(1) and
  // keeps the map at the cap regardless of age.
  if (recent.size >= DEDUPE_MAX) {
    recent.delete(recent.keys().next().value);
  }
  recent.set(key, now);
  return true;
}

/**
 * @param {object} p
 * @param {string} p.source  where it came from: 'fetch' | 'send' | 'draft-save' |
 *                           'boundary' | 'window.error' | 'unhandledrejection' | ...
 * @param {string} p.message human-readable, already stringified
 * @param {string} [p.route]  API path or logical route — drives grouping
 * @param {number} [p.status] HTTP status when the error came from a response
 * @param {string} [p.stack]
 */
export function reportError(p) {
  try {
    const message = String(p?.message || 'unknown');
    const source  = String(p?.source || 'unknown');
    const route   = p?.route ? String(p.route) : undefined;
    if (!shouldReport(`${source}|${message}|${route || ''}`)) return;

    const body = JSON.stringify({
      kind: 'client',
      source,
      message,
      route,
      status: Number.isFinite(p?.status) ? p.status : undefined,
      stack: p?.stack ? String(p.stack) : undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // keepalive so a report fired during unload still lands.
    // NOTE: this URL is skipped by the fetch interceptor's own reporting —
    // otherwise a failing reporter would report its own failure, forever.
    fetch(REPORT_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

// Convenience for a catch block that has an Error in hand.
export function reportCaught(source, err, extra = {}) {
  reportError({
    source,
    message: err?.message || String(err),
    stack:   err?.stack,
    ...extra,
  });
}

// True for the reporter's own endpoint. The fetch interceptor must not report
// failures of the thing that reports failures.
export function isReportUrl(url) {
  return typeof url === 'string' && url.includes(REPORT_URL);
}

// Collapse a request URL to a groupable route: drop the origin, the query
// string, and any path segment that's an opaque id. Without this,
// /api/mail/messages/AAMkAGY5.../reply is a different route for every message
// and the error list is one row per email instead of one row per bug.
export function apiRoute(url) {
  let path = String(url || '');
  try { path = new URL(path, window.location.origin).pathname; } catch { path = path.split('?')[0]; }
  return path
    .split('/')
    .map(seg => {
      if (!seg) return seg;
      if (/^\d+$/.test(seg)) return ':id';
      if (seg.length >= 24 && /^[A-Za-z0-9+/=_-]+$/.test(seg)) return ':id';   // Graph ids
      if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(seg)) return ':id';             // uuid
      return seg;
    })
    .join('/');
}
